/**
 * Polish strings of the logistics v2 features (`logOpt.*`). English in `logistics-optimization-en.ts` (typed against this one).
 *
 * Suggestion texts (`kind.<KIND>.text`) are interpolated with the formatted `facts` of the suggestion plus `{route}`,
 * `{customer}` and `{target}`. When a placeholder stays unresolved (the backend sent other facts) the UI shows `fallback`.
 */
export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const logOptPl = {
  view: 'Optymalizacja',
  subtitle: 'Gdzie flota traci kilometry i pieniądze — ocena tras, podpowiedzi i symulator „co jeśli”.',
  loadError: 'Nie udało się wczytać analizy tras',
  common: {
    estimate: 'Szacunek (linia prosta)',
    estimateHint: 'Serwer tras jest niedostępny — km liczone w linii prostej × 1,25.',
    roads: 'Po drogach',
    skipped: 'Bez współrzędnych: {count}',
    skippedHint: 'Dostawy do klientów bez współrzędnych nie są na mapie.',
    truncated: 'Za dużo punktów — część pominięta',
    depot: 'Magazyn',
    fit: 'Pokaż wszystko',
    perMonth: '{value}/mies.',
    noCostAccess: 'Koszty widzi tylko zakres całej firmy.',
    simulate: 'Symuluj',
    minutes: '{value} min',
    hours: '{h} h {m} min',
  },
  map: {
    aria: 'Mapa kursów po drogach',
    legend: 'Trasy dnia',
    all: 'Wszystkie',
    showRoute: 'Pokaż trasę {code}',
    stops: 'Przystanków: {count}',
    leg: 'Odcinek {km} · {min}',
    seq: 'Przystanek {seq}',
    loadingRoads: 'Wyznaczam trasy po drogach…',
  },
  run: {
    title: 'Kurs dnia',
    subtitle: 'Kolejność optymalna po drogach: magazyn → przystanki → magazyn',
    noRun: 'Trasa nie jechała w tym dniu',
    noRunBody: 'Wybierz inny dzień z listy kursów albo przełącz dzień strzałkami.',
    loadError: 'Nie udało się wyznaczyć kursu',
    distance: 'Dystans',
    duration: 'Czas jazdy',
    empty: 'Pusty przebieg',
    emptyHint: '{pct} kursu bez towaru u klienta',
    recorded: 'Km zapisane',
    optimal: 'Km optymalne',
    excess: '+{km} ponad optymalny objazd',
    onPlan: 'W granicach optymalnego objazdu',
    noRecorded: 'Brak km w kosztach tego dnia',
    timeline: 'Kolejność przystanków',
    timelineHint: 'Odcinek i czas od poprzedniego punktu',
    depotStart: 'Wyjazd z magazynu',
    depotEnd: 'Powrót do magazynu',
    emptyLeg: 'pusty przebieg',
    total: 'Razem',
    openDelivery: 'Otwórz dostawę: {customer}',
    stopsList: 'Dostawy kursu',
  },
  detail: {
    efficiency: 'Efektywność trasy',
    efficiencyHint: 'Ostatnie {days} dni na tle floty',
    noEfficiency: 'Brak danych o efektywności trasy w okresie.',
    suggestions: 'Podpowiedzi dla trasy',
    noSuggestions: 'Brak podpowiedzi — trasa jedzie efektywnie.',
    costs: 'Koszty 30 dni',
    costsHint: 'Dzienny koszt trasy z podziałem na składniki',
    simulateRoute: 'Symuluj trasę',
    toOptimization: 'Optymalizacja',
  },
  metric: {
    score: 'Ocena',
    fill: 'Wypełnienie',
    empty: 'Pusty przebieg',
    excess: 'Nadmiarowe km',
    kmPerDelivery: 'Km / dostawę',
    costPerDelivery: 'Koszt / dostawę',
    costShare: 'Koszt % przychodu',
    runsPerWeek: 'Kursy / tydz.',
    avgStops: 'Przystanki / kurs',
    revenuePerKm: 'Przychód / km',
  },
  hero: {
    saving: 'Potencjał oszczędności',
    savingHint: 'z {count} podpowiedzi',
    savingKm: 'Km do odzyskania',
    savingKmHint: 'miesięcznie',
    empty: 'Pusty przebieg floty',
    emptyHint: 'km magazyn ↔ klienci',
    excess: 'Nadmiarowe km',
    excessHint: 'ponad optymalny objazd',
    fill: 'Wypełnienie aut',
    fillHint: 'pojemność: {capacity} przystanków',
    costPerDelivery: 'Koszt / dostawę',
    costPerDeliveryHint: '{runs} kursów, {deliveries} dostaw',
    insightTop: 'Największa szansa: {title} — {saving}.',
    insightNone: 'Trasy jadą efektywnie — brak podpowiedzi w tym okresie.',
  },
  period: { label: 'Okres analizy', d30: '30 dni', d90: '90 dni', range: '{from} – {to}' },
  section: {
    suggestions: 'Podpowiedzi',
    suggestionsHint: 'Od największej oszczędności. „Symuluj” przelicza zmianę bez zapisu.',
    ranking: 'Ranking tras',
    rankingHint:
      'Najpierw trasy z największym polem do poprawy. Ocena 0–100: wypełnienie, pusty przebieg i koszt dostawy na tle floty.',
    weekdays: 'Obciążenie dni tygodnia',
    weekdaysHint: 'Średnie wypełnienie aut w dniu jazdy',
    compare: 'Mapa objazdu trasy',
    compareHint: 'Typowy objazd klientów trasy po drogach z magazynu',
  },
  severity: { HIGH: 'Wysoki priorytet', MEDIUM: 'Średni priorytet', LOW: 'Niski priorytet' },
  suggestion: {
    saving: 'Oszczędność',
    savingUnknown: 'oszczędność do oszacowania',
    openRoute: 'Trasa {code}',
    openCustomer: 'Karta klienta',
    all: 'Wszystkie',
    empty: 'Brak podpowiedzi',
    emptyBody: 'W wybranym okresie trasy jadą efektywnie. Sprawdź dłuższy okres albo symulator.',
    list: 'Lista podpowiedzi',
    filter: 'Filtr rodzaju podpowiedzi',
  },
  kind: {
    REORDER: {
      label: 'Kolejność',
      title: 'Lepsza kolejność przystanków',
      text: 'Trasa {route} przejeżdża średnio {recordedKm} na kurs, a optymalny objazd po drogach to {optimalKm} — o {excessKm} ({excessPct}) za dużo.',
      fallback:
        'Trasa {route} jeździ dłużej niż optymalny objazd po drogach. Ułóż przystanki w optymalnej kolejności.',
    },
    REDUCE_FREQUENCY: {
      label: 'Częstotliwość',
      title: 'Rzadsze kursy',
      text: 'Trasa {route} jeździ {runsPerWeek}× w tygodniu ({weekdays}) z wypełnieniem {fillPct}. Kursy tylko w: {newWeekdays} podniosą wypełnienie do {newFillPct}.',
      fallback: 'Trasa {route} jeździ z niskim wypełnieniem aut. Rzadsze kursy pomieszczą te same dostawy.',
    },
    REASSIGN_CUSTOMER: {
      label: 'Przypisanie',
      title: 'Klient na innej trasie',
      text: '{customer} leży {nearestOwnKm} od najbliższego klienta trasy {route}, a tylko {nearestTargetKm} od trasy {target}. Przeniesienie skraca {route} o {kmSavedPerVisit} na dostawę, a wydłuża {target} o {kmAddedPerVisit}.',
      fallback: '{customer} leży bliżej trasy {target} niż trasy {route}. Rozważ przeniesienie klienta.',
    },
    EMPTY_RUNS: {
      label: 'Pusty przebieg',
      title: 'Duży pusty przebieg',
      text: 'Na trasie {route} {emptyPct} kilometrów to dojazd z magazynu i powrót: {emptyKm} na kurs, {emptyKmMonthly} miesięcznie. Rozważ przewoźnika, punkt przeładunkowy albo rzadsze kursy.',
      fallback: 'Na trasie {route} duża część kilometrów to dojazd z magazynu i powrót bez towaru.',
    },
    UNPROFITABLE_DELIVERIES: {
      label: 'Rentowność',
      title: 'Nierentowne dostawy',
      text: 'Dostawa do {customer} kosztuje średnio {costPerDelivery}, a marża z dostawy to {marginPerDelivery} — miesięcznie {lossMonthly} straty. Rozważ minimum zamówienia lub rzadsze dostawy.',
      fallback:
        'Dostawy do {customer} kosztują więcej, niż wynosi marża z zamówień. Rozważ minimum zamówienia lub rzadsze dostawy.',
    },
    WEAK_WEEKDAY: {
      label: 'Dzień tygodnia',
      title: 'Słaby dzień jazdy',
      text: 'W {weekday} auta jeżdżą z wypełnieniem {fillPct}, w pozostałe dni {otherFillPct}. Dostawy z tego dnia zmieszczą się w innych kursach.',
      fallback: 'Jeden z dni tygodnia ma wyraźnie niższe wypełnienie aut. Przenieś jego dostawy na inne dni.',
    },
  },
  ranking: {
    sort: 'Sortowanie tras',
    by: { score: 'Ocena', fill: 'Wypełnienie', empty: 'Pusty przebieg', cost: 'Koszt / dostawę' },
    runsPerWeek: '{value}× / tydz.',
    deliveries: 'Dostaw: {count}',
    weekdays: 'Dni jazdy',
    showMap: 'Pokaż objazd na mapie',
    open: 'Szczegóły trasy',
    empty: 'Brak kursów w okresie',
    scoreAria: 'Ocena efektywności: {score} na 100',
  },
  band: { good: 'Efektywna', ok: 'Do poprawy', bad: 'Słaba' },
  weekday: { d1: 'poniedziałek', d2: 'wtorek', d3: 'środa', d4: 'czwartek', d5: 'piątek' },
  weekdayIn: { d1: 'poniedziałki', d2: 'wtorki', d3: 'środy', d4: 'czwartki', d5: 'piątki' },
  wd: { d1: 'pon', d2: 'wt', d3: 'śr', d4: 'czw', d5: 'pt' },
  chart: {
    fill: 'Wypełnienie',
    runs: 'Kursy',
    deliveries: 'Dostawy',
    avgStops: 'Przystanki / kurs',
    threshold: 'Próg niskiego wypełnienia',
    empty: 'Brak kursów w okresie',
    costsAria: 'Wykres dziennych kosztów trasy',
    weekdaysAria: 'Wykres wypełnienia aut według dnia tygodnia',
  },
  compare: {
    route: 'Trasa na mapie',
    customers: 'Klientów: {count}',
    loop: 'Objazd {km} · {time}',
    empty: 'Brak klientów trasy ze współrzędnymi',
    loadError: 'Nie udało się wyznaczyć objazdu trasy',
    deliveries: 'Dostaw: {count}',
    revenue: 'Przychód: {value}',
  },
  territory: {
    roads: 'Objazd po drogach · {km}',
    estimate: 'Szacunek (linia prosta) · {km}',
    fallback: 'Linia orientacyjna (najbliższy sąsiad)',
    truncated: 'Na objeździe najważniejsi klienci',
    hint: 'Linia pokazuje typowy objazd klientów trasy po drogach z magazynu i z powrotem (okres: {days} dni).',
  },
  sim: {
    open: 'Symulator',
    title: 'Symulator „co jeśli”',
    subtitle: 'Złóż zmiany i porównaj kursy, kilometry i koszty przed i po.',
    notice: 'Symulacja — nic nie zostało zmienione w planie tras ani w danych.',
    changes: 'Zmiany',
    add: 'Dodaj zmianę',
    remove: 'Usuń zmianę',
    empty: 'Dodaj co najmniej jedną zmianę, aby policzyć wariant.',
    run: 'Policz wariant',
    reset: 'Wyczyść',
    incomplete: 'Uzupełnij pola wszystkich zmian.',
    error: 'Nie udało się policzyć symulacji',
    days: 'Okres bazowy',
    kind: {
      MOVE_CUSTOMER: 'Przenieś klienta',
      SET_RUN_DAYS: 'Dni jazdy trasy',
      OPTIMIZE_ORDER: 'Optymalna kolejność',
      STOP_CAPACITY: 'Pojemność auta',
    },
    kindHint: {
      MOVE_CUSTOMER: 'Klient i jego dostawy przechodzą na inną trasę.',
      SET_RUN_DAYS: 'W które dni trasa wyjeżdża — dostawy pozostałych dni łączą się w wybrane.',
      OPTIMIZE_ORDER: 'Kursy trasy jadą w optymalnej kolejności po drogach.',
      STOP_CAPACITY: 'Ile przystanków mieści jedno auto na tej trasie.',
    },
    fromRoute: 'Z trasy',
    toRoute: 'Do trasy',
    route: 'Trasa',
    pickRoute: 'Wybierz trasę',
    customer: 'Klient',
    customerSearch: 'Szukaj klienta trasy…',
    noCustomers: 'Brak pasujących klientów',
    pickRouteFirst: 'Najpierw wybierz trasę źródłową',
    weekdays: 'Dni jazdy',
    capacity: 'Przystanków na auto',
    result: 'Wynik symulacji',
    before: 'Przed',
    after: 'Po',
    perRoute: 'Wynik per trasa',
    assumptions: 'Założenia modelu',
    warnings: 'Ostrzeżenia',
    noCost: 'Koszty niedostępne — widzi je tylko zakres całej firmy.',
    better: 'lepiej',
    worse: 'gorzej',
    metric: {
      runsPerMonth: 'Kursy / mies.',
      vansPerMonth: 'Auto-dni / mies.',
      kmPerMonth: 'Km / mies.',
      costPerMonth: 'Koszt / mies.',
      avgFillPct: 'Wypełnienie',
      avgStopsPerRun: 'Przystanki / kurs',
      deliveriesPerMonth: 'Dostawy / mies.',
    },
  },
  assumption: {
    generic: '{key}: {value}',
    stopCapacity: 'Pojemność auta: {value} przystanków',
    workingDaysPerMonth: 'Dni robocze w miesiącu: {value}',
    periodDays: 'Okres bazowy: {value} dni',
    sampleRuns: 'Kursy w próbce OSRM na trasę: {value}',
    kmModel: 'Km kursu: dojazd z magazynu + część rosnąca z liczbą przystanków (√n)',
    kmSource: 'Odległości: {value}',
    fixedCostPerVanDay: 'Stały koszt auta na dzień (kierowca + pojazd): {value}',
    costPerKm: 'Koszt kilometra: {value}',
    costPerStop: 'Koszt przystanku: {value}',
  },
  source: {
    osrm: 'po drogach (OSRM)',
    straight: 'linia prosta × 1,25',
    mixed: 'po drogach i szacunek',
    none: 'brak danych',
  },
  warning: {
    generic: 'Uwaga: {key}',
    noRuns: 'Trasa nie ma kursów w okresie — zmiana nie ma na czym się oprzeć.',
    fewerVisits: 'Na trasie docelowej klient miałby mniej dostaw w tygodniu niż dotąd.',
    noKm: 'Brak kilometrów trasy — wynik szacunkowy.',
    noRecordedKm: 'Brak km zapisanych w kosztach — porównanie z optymalnym objazdem niemożliwe.',
    overCapacity: 'Przekroczona pojemność auta — potrzebne dodatkowe auto.',
    noDeliveries: 'Klient nie ma dostaw w okresie — zmiana pominięta.',
    sameRoute: 'Klient już jeździ tą trasą — zmiana pominięta.',
    noCoords: 'Klient bez współrzędnych — jego kilometry nie zostały policzone.',
  },
};
